import { Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FEATURE_COMPARISON_PLANS } from '@/Domain/components/Subscription/FeatureComparison/featureComparison.config';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { useGetSubscriptionPlans } from '@/Domain/hooks/Subscription/useGetSubscriptionPlans.hook';
import { useUpdateSubscription } from '@/Domain/hooks/Subscription/useUpdateSubscription.hook';

type TierKey = 'starter' | 'basic' | 'essential' | 'professional' | 'business';
type ProductKey =
  | 'starter'
  | 'basic_2026'
  | 'essential_2026'
  | 'professional_2026'
  | 'business_2026';

interface Tier {
  id: TierEnum;
  // Translation key + test-id suffix.
  key: TierKey;
  // `SubscriptionPlansDto.products` / offer-endpoint key.
  productKey: ProductKey;
  highlighted?: boolean;
}

const TIERS: readonly Tier[] = [
  { id: TierEnum.STARTER, key: 'starter', productKey: 'starter' },
  { id: TierEnum.BASIC_2026, key: 'basic', productKey: 'basic_2026' },
  { id: TierEnum.ESSENTIAL_2026, key: 'essential', productKey: 'essential_2026' },
  {
    id: TierEnum.PROFESSIONAL_2026,
    key: 'professional',
    productKey: 'professional_2026',
    highlighted: true,
  },
  { id: TierEnum.BUSINESS_2026, key: 'business', productKey: 'business_2026' },
] as const;

// Order of the tiers shown here. Free isn't included, so the first card
// (Starter) is the baseline and later tiers chain off the previous shown tier.
const TIER_ORDER: readonly TierKey[] = TIERS.map((tier) => tier.key);

// Display strings; `products[tier].price_monthly` from the API is the Stripe price ID, not a price.
const DISPLAY_PRICE_BY_TIER: Record<ProductKey, string> = Object.fromEntries(
  FEATURE_COMPARISON_PLANS.filter(
    (p): p is typeof p & { productKey: ProductKey } => p.productKey !== null,
  ).map((p) => [p.productKey, p.price.monthly]),
) as Record<ProductKey, string>;

interface PricingStepProps {
  source?: string;
  discountApplied?: boolean;
  discountPercentOff?: number;
  promoCode?: string;
}

function applyDiscountToDisplayPrice(price: string, percentOff: number): string | null {
  const match = price.match(/^([^\d]*)(\d+(?:\.\d+)?)(.*)$/);
  if (!match) return null;
  const [, prefix, amount, suffix] = match;
  const raw = Number(amount) * (1 - percentOff / 100);
  // 50% off an odd marketing price ($9, $29…) lands on a half-dollar; show cents so it isn't rounded up to understate the discount.
  const next = Number.isInteger(raw) ? String(raw) : raw.toFixed(2);
  return `${prefix}${next}${suffix}`;
}

export const PricingStep = ({
  source = 'getting_started',
  discountApplied = false,
  discountPercentOff = 0,
  promoCode,
}: PricingStepProps) => {
  const { t } = useTranslation(['getting_started', 'subscription_page']);
  const { data: plans } = useGetSubscriptionPlans();
  const [pendingTier, setPendingTier] = useState<TierEnum | null>(null);

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
      data-testid="gs-pricing-tiers"
    >
      {TIERS.map((tier) => (
        <TierCard
          key={tier.key}
          tier={tier}
          stripePriceId={plans?.products[tier.productKey]?.price_monthly}
          pendingTier={pendingTier}
          setPendingTier={setPendingTier}
          source={source}
          discountApplied={discountApplied}
          discountPercentOff={discountPercentOff}
          promoCode={promoCode}
          t={t}
        />
      ))}
    </div>
  );
};

interface TierCardProps {
  tier: Tier;
  stripePriceId: string | undefined;
  pendingTier: TierEnum | null;
  setPendingTier: (tier: TierEnum | null) => void;
  source: string;
  discountApplied: boolean;
  discountPercentOff: number;
  promoCode?: string;
  t: ReturnType<typeof useTranslation>['t'];
}

const TierCard = ({
  tier,
  stripePriceId,
  pendingTier,
  setPendingTier,
  source,
  discountApplied,
  discountPercentOff,
  promoCode,
  t,
}: TierCardProps) => {
  const updateSubscription = useUpdateSubscription({
    successCallback: () => setPendingTier(null),
    // Stripe outage (or any 4xx/5xx from `/payments/handle-subscription/`):
    // re-enable the tier cards and surface a toast. Without this, the local
    // `pendingTier` state stays set and the user is stranded with a
    // permanently-spinning button + the other tiers disabled.
    errorCallback: () => {
      setPendingTier(null);
      toast.error(t('pricing_step.checkout_error_toast'));
    },
    fromTier: TierEnum.FREE,
    toTier: tier.id,
    interval: SubscriptionIntervalEnum.Monthly,
    source,
    promotionCode: discountApplied ? promoCode : undefined,
  });

  const handleChoose = async () => {
    if (!stripePriceId || pendingTier) return;
    setPendingTier(tier.id);
    void updateSubscription.handleUpdate(stripePriceId);
  };

  // Reuse the billing page's pricing-card copy/indexes, but chain over the
  // tiers shown here (Free isn't), so Starter lists its full baseline and each
  // later tier shows only what it adds over the previous shown tier.
  const featuresFor = (key: string): string[] =>
    t(`subscription_page:pricing.plans.${key}.features`, {
      returnObjects: true,
    }) as unknown as string[];
  const chainIndex = TIER_ORDER.indexOf(tier.key);
  const previousKey = chainIndex > 0 ? TIER_ORDER[chainIndex - 1] : null;
  const previousFeatures = previousKey ? featuresFor(previousKey) : [];
  const displayedFeatures = featuresFor(tier.key).filter(
    (feature) => !previousFeatures.includes(feature),
  );
  const inheritsFrom = previousKey ? t(`subscription_page:tiers.${previousKey}`) : null;

  const isPending = pendingTier === tier.id;
  const isDisabled = pendingTier !== null && !isPending;
  const borderClass = tier.highlighted ? 'border-primary/40' : 'border-border';
  const displayPrice = DISPLAY_PRICE_BY_TIER[tier.productKey];
  const discountedDisplayPrice = discountApplied
    ? applyDiscountToDisplayPrice(displayPrice, discountPercentOff)
    : null;

  return (
    <div
      className={`relative flex min-w-0 flex-col gap-3 rounded-xl border ${borderClass} bg-card p-4`}
      data-testid={`gs-pricing-card-${tier.key}`}
    >
      {tier.highlighted && (
        <span
          className="absolute -top-2 right-3 rounded-full bg-info-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-info"
          data-testid={`gs-pricing-card-badge-${tier.key}`}
        >
          {t('pricing_step.popular_badge')}
        </span>
      )}
      <p className="text-base font-semibold text-foreground">
        {t(`pricing_step.tiers.${tier.key}.name`)}
      </p>

      <div className="flex flex-col">
        <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          {discountedDisplayPrice ? (
            <>
              <span
                className="whitespace-nowrap text-xl font-semibold text-foreground"
                data-testid={`gs-pricing-card-price-${tier.key}`}
              >
                {discountedDisplayPrice}
              </span>
              <span
                className="whitespace-nowrap text-sm font-medium text-muted-foreground line-through"
                data-testid={`gs-pricing-card-original-price-${tier.key}`}
              >
                {displayPrice}
              </span>
            </>
          ) : (
            <span
              className="whitespace-nowrap text-2xl font-semibold text-foreground"
              data-testid={`gs-pricing-card-price-${tier.key}`}
            >
              {displayPrice}
            </span>
          )}
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {t('pricing_step.per_month')}
          </span>
          <span
            className="whitespace-nowrap text-xs text-foreground-subtle"
            data-testid={`gs-pricing-card-tax-note-${tier.key}`}
          >
            {t('subscription_page:pricing.excl_vat')}
          </span>
        </div>
        {discountedDisplayPrice && (
          <span
            className="text-[11px] font-medium text-info"
            data-testid={`gs-pricing-card-discount-label-${tier.key}`}
          >
            {t('pricing_step.discount_first_invoice')}
          </span>
        )}
      </div>

      {inheritsFrom && (
        <p className="text-xs font-medium text-foreground">
          {t('subscription_page:pricing.everything_in', { plan: inheritsFrom })}
        </p>
      )}
      <ul className="flex flex-col gap-1.5">
        {displayedFeatures.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-xs text-foreground">
            <Check className="size-3.5 mt-0.5 shrink-0 text-success" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        variant={tier.highlighted ? 'dark' : 'secondary'}
        size="sm"
        onClick={() => void handleChoose()}
        disabled={isDisabled || !stripePriceId}
        loading={isPending}
        className="mt-auto w-full"
        data-testid={`gs-pricing-card-choose-${tier.key}`}
      >
        {t('pricing_step.choose_plan')}
      </Button>
    </div>
  );
};
